import api from './api.service';

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface SupplierInquiryLine {
  id: string;
  supplier_inquiry_id: string;
  line_number: number;
  purchase_requisition_id: string;
  purchase_requisition_item_id: string;
  item_code?: string;
  item_description: string;
  quantity: number;
  uom?: string;
  required_date?: string;
  project_id?: string;
  project?: { id: string; project_number?: string; project_name?: string } | null;
  specifications?: string;
  remarks?: string;
}

export interface SupplierInquirySupplier {
  id: string;
  supplier_inquiry_id: string;
  supplier_id: string;
  access_token: string;
  status: string;
  supplier_contact?: string;
  supplier_email?: string;
  supplier?: { id: string; supplier_name: string; supplier_code: string };
}

export interface SupplierInquiry {
  id: string;
  company_id: string;
  inquiry_number: string;
  inquiry_date: string;
  source_type: string;
  requester_id?: string;
  department_id?: string;
  response_deadline?: string;
  expected_delivery_date?: string;
  currency: string;
  remarks?: string;
  status: string;
  created_at?: string;
  /** Non-deleted supplier quotations linked to this inquiry (from API; used for delete rules). */
  active_quotation_count?: number;
  lines?: SupplierInquiryLine[];
  inquirySuppliers?: SupplierInquirySupplier[];
}

export interface CreateSupplierInquiryPayload {
  inquiry_date: string;
  response_deadline?: string;
  expected_delivery_date?: string;
  currency?: string;
  remarks?: string;
  department_id?: string;
  purchase_requisition_ids: string[];
  pr_line_ids: string[];
  supplier_ids: string[];
  supplier_contacts?: Record<string, string>;
  supplier_emails?: Record<string, string>;
}

export class SupplierInquiryService {
  async list(params?: { status?: string; from_date?: string; to_date?: string; supplier_id?: string; pr_number?: string }): Promise<SupplierInquiry[]> {
    const q = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') q.set(k, String(v)); });
    const res = await api.get(`/procurement/supplier-inquiries?${q.toString()}`);
    const raw = unwrap<SupplierInquiry[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async getById(id: string): Promise<SupplierInquiry> {
    const res = await api.get(`/procurement/supplier-inquiries/${id}`);
    return unwrap<SupplierInquiry>(res);
  }

  async create(payload: CreateSupplierInquiryPayload): Promise<SupplierInquiry> {
    const res = await api.post('/procurement/supplier-inquiries', payload);
    return unwrap<SupplierInquiry>(res);
  }

  async getLink(inquirySupplierId: string): Promise<{ link: string; token: string }> {
    const res = await api.get(`/procurement/supplier-inquiries/link/${inquirySupplierId}`);
    return unwrap<{ link: string; token: string }>(res);
  }

  async updateStatus(id: string, status: string): Promise<SupplierInquiry> {
    const res = await api.put(`/procurement/supplier-inquiries/${id}/status`, { status });
    return unwrap<SupplierInquiry>(res);
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/procurement/supplier-inquiries/${id}`);
  }
}

export const supplierInquiryService = new SupplierInquiryService();
